/** Inline entity label helpers (no label master table). */

export const MAX_ENTITY_LABEL_LENGTH = 80;

/** Sentinel activeLabel value for the “Unlabeled” filter chip (not a real label name). */
export const ENTITY_LABEL_FILTER_UNLABELED = '__entity_label_filter_unlabeled__';

export const ENTITY_LABEL_FILTER_UNLABELED_DISPLAY = 'Unlabeled';

export function isUnlabeledEntityLabelFilter(activeLabel) {
  return activeLabel === ENTITY_LABEL_FILTER_UNLABELED;
}

export function normalizeEntityLabelName(label) {
  return label != null ? String(label).trim() : '';
}

/**
 * @returns {{ ok: true, label: string } | { ok: false, error: string }}
 */
export function validateEntityLabelName(label) {
  const trimmed = normalizeEntityLabelName(label);
  if (!trimmed) return { ok: false, error: 'Label name is required' };
  if (trimmed.length > MAX_ENTITY_LABEL_LENGTH) {
    return { ok: false, error: `Label must be ${MAX_ENTITY_LABEL_LENGTH} characters or fewer` };
  }
  return { ok: true, label: trimmed };
}

/** @param {unknown} raw */
export function normalizeEntityLabelsArray(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set();
  const out = [];
  for (const entry of raw) {
    const trimmed = normalizeEntityLabelName(entry);
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

/** Distinct values from an array field (`labels`) or a scalar string field (`category`). */
export function getItemLabels(item, labelField = 'labels') {
  if (!item) return [];
  const raw = item[labelField];
  // Scalar fields (e.g. asset `category`) share the same chip filter as label arrays.
  if (typeof raw === 'string') {
    const trimmed = normalizeEntityLabelName(raw);
    return trimmed ? [trimmed] : [];
  }
  return normalizeEntityLabelsArray(raw);
}

/** Distinct non-empty labels on items, sorted. */
export function getDistinctLabelsFromItems(items, labelField = 'labels') {
  const names = new Set();
  for (const item of items || []) {
    for (const name of getItemLabels(item, labelField)) {
      names.add(name);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

/** Union of labels on items plus user-added names (not yet on any item). */
export function buildEntityLabelOptions(items, extraLabelNames = [], labelField = 'labels') {
  const names = new Set(getDistinctLabelsFromItems(items, labelField));
  for (const name of extraLabelNames || []) {
    const trimmed = normalizeEntityLabelName(name);
    if (trimmed) names.add(trimmed);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

export function itemHasLabel(item, labelName, labelField = 'labels') {
  const target = normalizeEntityLabelName(labelName);
  if (!target) return false;
  return getItemLabels(item, labelField).includes(target);
}

export function countItemsWithLabel(items, labelName, labelField = 'labels') {
  const target = normalizeEntityLabelName(labelName);
  if (!target) return 0;
  return (items || []).filter((item) => itemHasLabel(item, target, labelField)).length;
}

export function itemHasNoLabels(item, labelField = 'labels') {
  return getItemLabels(item, labelField).length === 0;
}

export function countItemsWithoutLabels(items, labelField = 'labels') {
  return (items || []).filter((item) => itemHasNoLabels(item, labelField)).length;
}

/**
 * Labels to assign when creating an item while a filter chip is active.
 * @returns {string[] | undefined}
 */
export function getLabelsForNewItemFromActiveFilter(activeLabel) {
  if (activeLabel == null || isUnlabeledEntityLabelFilter(activeLabel)) return undefined;
  const target = normalizeEntityLabelName(activeLabel);
  return target ? [target] : undefined;
}

/**
 * @param {string | null | undefined} activeLabel — null/empty = show all
 */
export function filterItemsByEntityLabel(items, activeLabel, labelField = 'labels') {
  if (isUnlabeledEntityLabelFilter(activeLabel)) {
    return (items || []).filter((item) => itemHasNoLabels(item, labelField));
  }
  const target = normalizeEntityLabelName(activeLabel);
  if (!target) return items || [];
  return (items || []).filter((item) => itemHasLabel(item, target, labelField));
}

export function formatItemLabelsSummary(item, labelField = 'labels') {
  const labels = getItemLabels(item, labelField);
  return labels.length > 0 ? labels.join(', ') : 'No labels';
}

export function entityLabelsEqual(left, right) {
  const a = normalizeEntityLabelsArray(left);
  const b = normalizeEntityLabelsArray(right);
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
